import { useState } from 'react';
import CampoTexto from '@/components/base/CampoTexto';

export type TipoChave = 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria';

export interface DadosRecebedor {
  nome: string;
  tipo_chave: TipoChave;
  chave_pix: string;
  documento: string;
  banco: string;
}

const TIPOS_CHAVE: { valor: TipoChave; rotulo: string }[] = [
  { valor: 'cpf', rotulo: 'CPF' },
  { valor: 'cnpj', rotulo: 'CNPJ' },
  { valor: 'email', rotulo: 'E-mail' },
  { valor: 'telefone', rotulo: 'Telefone' },
  { valor: 'aleatoria', rotulo: 'Aleatória' },
];

const BANCOS = [
  'Mercado Pago',
  'Banco do Brasil',
  'Caixa',
  'Itaú',
  'Bradesco',
  'Santander',
  'Nubank',
  'Sicoob',
  'Sicredi',
  'PicPay',
  'PagSeguro',
  'Outro',
];

interface FormRecebedorProps {
  erro: string | null;
  salvando: boolean;
  aoSalvar: (dados: DadosRecebedor) => void;
  aoCancelar?: () => void;
}

export default function FormRecebedor({
  erro,
  salvando,
  aoSalvar,
  aoCancelar,
}: FormRecebedorProps) {
  const [nome, setNome] = useState('');
  const [tipoChave, setTipoChave] = useState<TipoChave>('cpf');
  const [chavePix, setChavePix] = useState('');
  const [documento, setDocumento] = useState('');
  const [banco, setBanco] = useState('');
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  function submeter() {
    if (!nome.trim()) {
      setErroLocal('Diga quem vai receber.');
      return;
    }
    if (!chavePix.trim()) {
      setErroLocal('Informe a chave Pix.');
      return;
    }
    setErroLocal(null);
    aoSalvar({
      nome: nome.trim(),
      tipo_chave: tipoChave,
      chave_pix: chavePix.trim(),
      documento: documento.trim(),
      banco,
    });
  }

  return (
    <div className="space-y-5">
      <CampoTexto
        id="recebedor-nome"
        name="recebedor_nome"
        label="Quem recebe"
        help="O nome da paróquia, da pastoral ou da pessoa."
        value={nome}
        onChange={(v) => {
          setNome(v);
          if (erroLocal) setErroLocal(null);
        }}
        placeholder="Paróquia São João Batista"
      />

      <div>
        <span className="block text-base font-medium text-foreground-950">
          Tipo da chave Pix
        </span>
        <div className="mt-2 flex flex-wrap gap-2">
          {TIPOS_CHAVE.map((t) => (
            <button
              key={t.valor}
              type="button"
              onClick={() => setTipoChave(t.valor)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                tipoChave === t.valor
                  ? 'bg-primary-500 text-white'
                  : 'bg-secondary-100 text-foreground-700 hover:bg-secondary-200'
              }`}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
      </div>

      <CampoTexto
        id="recebedor-chave"
        name="recebedor_chave"
        label="Chave Pix"
        help="A chave que vai receber os repasses. Se for do Mercado Pago, cai na hora."
        value={chavePix}
        onChange={(v) => {
          setChavePix(v);
          if (erroLocal) setErroLocal(null);
        }}
        placeholder="sua-chave-pix"
      />

      <CampoTexto
        id="recebedor-documento"
        name="recebedor_documento"
        label="CPF ou CNPJ de quem recebe"
        help="Ajuda a conferir que o Pix é mesmo dessa pessoa ou entidade."
        value={documento}
        onChange={setDocumento}
        placeholder="000.000.000-00"
      />

      <div>
        <label htmlFor="recebedor-banco" className="block text-base font-medium text-foreground-950">
          Banco
        </label>
        <p className="mt-1 text-sm text-secondary-500">Opcional, só informativo.</p>
        <select
          id="recebedor-banco"
          value={banco}
          onChange={(e) => setBanco(e.target.value)}
          className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
        >
          <option value="">Selecione</option>
          {BANCOS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </div>

      {(erroLocal || erro) && (
        <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700" data-erro="true">
          {erroLocal || erro}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={submeter}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar quem recebe'}
        </button>
        {aoCancelar && (
          <button
            type="button"
            onClick={aoCancelar}
            disabled={salvando}
            className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}